import { API_URL } from '@env';
import { ToastrService } from 'ngx-toastr';
import { Observable, throwError } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';

import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { ISnListQueryParameters, ISnListResponse } from '@lib/api';
import { FsStateConvertPipe } from '@lib/common/pipes/state-convert.pipe';
import { t } from '@lib/translation';

import { ViApiError } from '../../shared/models/vi-api-error.model';
import {
  LprExportTriggerRequest,
  LprExportTriggerResponse,
} from '../interfaces/lpr-exports.interface';
import { LprExport } from '../models/lpr-export.model';

@Injectable({ providedIn: 'root' })
export class LprExportServiceAPI {
  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _toastrService: ToastrService,
  ) {}

  /**
   * Creates an LPR export report
   * @param data Export request data
   * @returns Observable of export response
   */
  public createLprReport(data: LprExportTriggerRequest): Observable<LprExportTriggerResponse> {
    return this._httpClient
      .post<LprExportTriggerResponse>(`${API_URL}/api/lpr/reports/v2/reads/`, data, {
        observe: 'response',
      })
      .pipe(
        tap((response) => {
          if (response.status === 201) {
            this._showExportSuccessToast();
          } else {
            this._showExportFailedToast();
          }
        }),
        map((response) => response.body!),
        catchError(this._handleError),
      );
  }

  /**
   * Retrieves a paginated list of LPR export reports.
   *
   * This method fetches LPR export data from the backend API, supporting pagination
   * and filtering through query parameters. The response includes metadata about
   * the total count and number of pages, along with the export results.
   *
   * @param params - Query parameters for filtering and pagination
   *
   * @returns An Observable that emits a list response containing:
   *   - count: Total number of exports matching the query
   *   - totalPages: Total number of pages available
   *   - results: Array of LprExport model instances
   *
   */
  public getExports(params: ISnListQueryParameters): Observable<ISnListResponse<LprExport>> {
    return this._httpClient
      .get<ISnListResponse<LprExport>>(`${API_URL}/api/lpr/reports/v2/reads`, { params })
      .pipe(
        map((response) => ({
          count: response.count,
          totalPages: response.totalPages,
          results: response.results.map((lprExport) => new LprExport(lprExport)),
        })),
        catchError(this._handleError),
      );
  }

  /**
   * Retries a failed LPR export report generation.
   *
   * This method triggers a retry operation for a specific LPR export report that
   * has previously failed. It sends a POST request to the retry endpoint with the
   * report ID, which will attempt to regenerate the export report with the same
   * parameters as the original request.
   *
   * @param reportId - The unique identifier of the LPR export report to retry
   *
   * @returns An Observable that emits the updated LprExport model instance
   *   containing the retry status and any updated metadata
   *
   * @throws ViApiError when the retry operation fails or the report ID is invalid
   *
   */
  public retryExport(reportId: string): Observable<LprExport> {
    return this._httpClient
      .post<LprExport>(`${API_URL}/api/lpr/reports/v2/reads/${reportId}/retry/`, {})
      .pipe(
        map((response) => new LprExport(response)),
        catchError(this._handleError),
      );
  }

  /**
   * Shows success toast with clickable link to view exports
   * @private
   */
  private _showExportSuccessToast(): void {
    const toast = this._toastrService.success(t('alpr.export.csvExportToast.success'), '', {
      enableHtml: true,
      tapToDismiss: false,
      closeButton: true,
      timeOut: 8000,
    });
  }

  /**
   * Shows error toast when export fails
   * @private
   */
  private _showExportFailedToast(): void {
    const toast = this._toastrService.error(t('alpr.export.csvExportToast.failed'), '', {
      enableHtml: true,
      tapToDismiss: false,
      closeButton: true,
      timeOut: 8000,
    });
  }

  private _handleError = (httpErrorResponse: HttpErrorResponse): Observable<never> => {
    const viError = {
      code: httpErrorResponse.status,
      message: httpErrorResponse.message || httpErrorResponse.error.message,
      original: httpErrorResponse,
    } as ViApiError;
    return throwError(() => viError);
  };
}
